import { AlertFactory } from 'alerts/Alert'
import BillingStore from 'billing/BillingStore'
import { NewPaymentMethodDelegate } from 'billing/payment-methods/PaymentMethodsRootViewModel'
import DocumentAccessStore from 'documents/DocumentAccessStore'
import { DocumentActionsAdapter } from 'documents/DocumentActionsAdapter'
import DocumentStore from 'documents/DocumentStore'
import DocumentTagStore from 'documents/DocumentTagStore'
import DocumentVersionStore from 'documents/DocumentVersionStore'
import { ViewDocumentDetailsDelegate } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import InvitationStore from 'iam/InvitationStore'
import MemberStore from 'iam/MemberStore'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import IAMProviderStore from 'iam/ProviderStore'
import RoleStore from 'iam/RoleStore'
import { GetCurrentUser } from 'iam/SessionStore'
import SkillStore from 'iam/SkillStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import TeamRoleStore from 'iam/TeamRoleStore'
import TeamStore from 'iam/TeamStore'
import UserDataStore from 'iam/UserDataStore'
import WorkspaceSelectorState from 'iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import link from 'infra/link'
import JobStore from 'jobs/JobStore'
import { action, observable, makeObservable } from 'mobx'
import NotificationPreferencesStore from 'notifications/NotificationPreferencesStore'
import PayoutMethodStore from 'payouts/pro/PayoutMethodStore'
import { Observable } from 'rxjs'
import { Id, Router } from 'types/definitions'
import { assertNotNil } from 'utils/error-util'
import ViewController from 'ViewController'

export default class ContextController {
  /**
   * A Map of workspace IDs <-> View Controller.
   */
  controllers = new Map<string, ViewController>()

  /**
   * The currently selected view controller.
   */
  @observable current: ViewController | null = null

  constructor(
    private newAlert: AlertFactory,
    private notificationPreferencesStore: NotificationPreferencesStore,
    private memberStore: MemberStore,
    private jobStore: JobStore,
    private teamStore: TeamStore,
    private teamMemberStore: TeamMemberStore,
    private teamRoleStore: TeamRoleStore,
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentAccessStore: DocumentAccessStore,
    private documentTagStore: DocumentTagStore,
    private invitationStore: InvitationStore,
    private roleStore: RoleStore,
    private skillStore: SkillStore,
    private billingStore: BillingStore,
    private workspaceStore: WorkspaceStore,
    private getCurrentUser: GetCurrentUser,
    private currentMember$: Observable<Member>,
    private router: Router,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private viewDocumentDetails: ViewDocumentDetailsDelegate,
    private newPaymentMethod: NewPaymentMethodDelegate,
    private documentActionsAdapter: DocumentActionsAdapter,
    private payoutMethodStore: PayoutMethodStore,
    private iamProviderStore: IAMProviderStore,
    private userDataStore: UserDataStore,
    private getWorkspaceSelectorState?: () =>
      | WorkspaceSelectorState
      | undefined,
  ) {
    makeObservable(this)
    currentMember$.subscribe((member) => this.changeWorkspace(member.workspace))
  }

  @action
  changeWorkspace(workspace: Workspace) {
    if (this.current) {
      this.current.setLastPath(this.router.location.pathname)
    }
    const existing = this.controllers.get(Id.assert(workspace.id))
    if (existing) {
      this.setViewController(existing)
    } else {
      this.controllers.set(
        Id.assert(workspace.id),
        this.createViewController(workspace),
      )
    }

    const controller = this.controllers.get(Id.assert(workspace.id))
    if (controller) {
      this.setViewController(controller)
    }
  }

  /**
   * Sets the current view controller.
   * @param controller
   */
  @action
  private setViewController(controller: ViewController) {
    this.current = controller
    if (this.controllers.size > 1) {
      this.router.replace(this.current.lastPath || link.welcome())
    }
  }

  /**
   * Creates a new View Controller instance.
   * @param workspace
   */
  private createViewController(workspace: Workspace) {
    const member = assertNotNil(
      this.memberStore.getMember(workspace.id, this.getCurrentUser().id),
      `Attempted to switch to a workspace but the current user's member has not been loaded yet.`,
    )

    return new ViewController(
      this.newAlert,
      this.notificationPreferencesStore,
      this.memberStore,
      this.jobStore,
      this.teamStore,
      this.teamMemberStore,
      this.teamRoleStore,
      this.documentStore,
      this.documentVersionStore,
      this.documentAccessStore,
      this.documentTagStore,
      this.invitationStore,
      this.roleStore,
      this.skillStore,
      this.billingStore,
      this.workspaceStore,
      member,
      this.router,
      this.getCurrentUserWorkspaceMemberships,
      this.currentMember$,
      this.viewDocumentDetails,
      this.newPaymentMethod,
      this.documentActionsAdapter,
      this.payoutMethodStore,
      this.iamProviderStore,
      this.userDataStore,
      this.getWorkspaceSelectorState,
    )
  }
}
